import { useEffect, useState } from "react";
import axios from "axios";
import { useForm, Controller } from "react-hook-form";
import { Dialog, DialogContent, DialogTitle, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { Close, Visibility, VisibilityOff } from "@mui/icons-material";
import { Mail, Save, Send } from "react-feather";
import { authData } from "../../components/GetAuth";
import styles from "./companyprofile.module.css";
import popupStyles from "../../components/popups/Popup.module.css";

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

// Port each encryption mode normally uses; picking a mode fills it in unless the admin typed a custom port
const DEFAULT_PORTS = { none: 25, ssl: 465, tls: 587 };

const emptyValues = {
  smtp_host: "",
  smtp_port: "",
  smtp_username: "",
  smtp_password: "",
  smtp_encryption: "tls",
  smtp_from_address: "",
  smtp_from_name: "",
};

const SmtpSettings = ({ settings, active, defaultTestEmail, onSaved, showMessage }) => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;

  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testError, setTestError] = useState("");
  const [testing, setTesting] = useState(false);

  const { handleSubmit, control, reset, getValues, setValue, trigger, formState: { errors } } = useForm({
    mode: "onChange",
    defaultValues: emptyValues,
  });

  const hasPassword = !!settings?.has_password;
  const isConfigured = !!settings?.smtp_host;

  useEffect(() => {
    if (settings) {
      reset({
        ...emptyValues,
        smtp_host: settings.smtp_host || "",
        smtp_port: settings.smtp_port || "",
        smtp_username: settings.smtp_username || "",
        smtp_encryption: settings.smtp_encryption || "tls",
        smtp_from_address: settings.smtp_from_address || "",
        smtp_from_name: settings.smtp_from_name || "",
      });
    }
  }, [settings, reset]);

  const onEncryptionChange = (value, field) => {
    field.onChange(value);
    const port = Number(getValues("smtp_port"));
    if (!port || Object.values(DEFAULT_PORTS).includes(port)) {
      setValue("smtp_port", DEFAULT_PORTS[value], { shouldValidate: true });
    }
  };

  const onSubmit = (data) => {
    setSaving(true);
    axios.post(process.env.REACT_APP_API_URL + "v1/company-settings/smtp", data, {
      headers: { Authorization: `Bearer ${API_TOKEN}` }
    }).then((response) => {
      setSaving(false);
      if (response.data.status === "SUCCESS") {
        showMessage(response.data.message, "success");
        onSaved(response.data.data);
      } else {
        showMessage(response.data.message, "error");
      }
    }).catch(() => {
      setSaving(false);
      showMessage("Could not save SMTP settings. Please try again.", "error");
    });
  };

  const openTest = async () => {
    const valid = await trigger();
    if (!valid) {
      showMessage("Please fix the highlighted SMTP fields first.", "error");
      return;
    }
    setTestEmail(defaultTestEmail || "");
    setTestError("");
    setTestOpen(true);
  };

  const sendTest = (e) => {
    e.preventDefault();
    if (!EMAIL_REGEX.test(testEmail)) {
      setTestError("Enter a valid email address");
      return;
    }

    setTesting(true);
    axios.post(process.env.REACT_APP_API_URL + "v1/company-settings/smtp/test", { ...getValues(), test_email: testEmail }, {
      headers: { Authorization: `Bearer ${API_TOKEN}` }
    }).then((response) => {
      setTesting(false);
      if (response.data.status === "SUCCESS") {
        setTestOpen(false);
        showMessage(response.data.message, "success");
      } else {
        setTestError(response.data.message);
      }
    }).catch(() => {
      setTesting(false);
      setTestError("Could not reach the server. Please try again.");
    });
  };

  return (<>
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <section className={`${styles.Card}`}>
        <div className={`${styles.CardHead}`}>
          <div>
            <h2 className={`${styles.CardTitle}`}><Mail />SMTP configuration</h2>
            <p className={`${styles.CardSubtitle}`}>Send booking and customer emails from your own mail server. Ask your email provider for these details.</p>
          </div>
          <span className={`${styles.StatusPill} ${isConfigured && active ? styles.StatusOn : styles.StatusOff}`}>{!isConfigured ? "Not configured" : active ? "Active" : "Configured"}</span>
        </div>

        <div className={`${styles.CardBody}`}>
          <div className={`${styles.FormGrid}`}>
            <p className={`${styles.GroupLabel}`}>Server</p>

            <div className={`${styles.FormFull}`}>
              <Controller
                name="smtp_host"
                control={control}
                rules={{ required: "SMTP host is required" }}
                render={({ field }) => (<TextField {...field} label="SMTP Host*" placeholder="smtp.example.com" variant="outlined" fullWidth autoComplete="off" error={!!errors.smtp_host} />)}
              />
              {errors.smtp_host && (<span className={`${styles.FieldError}`}>{errors.smtp_host.message}</span>)}
            </div>

            <div>
              <FormControl fullWidth>
                <InputLabel id="smtp-encryption-label">Encryption*</InputLabel>
                <Controller
                  name="smtp_encryption"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} labelId="smtp-encryption-label" label="Encryption*" onChange={(e) => onEncryptionChange(e.target.value, field)}>
                      <MenuItem value="tls">TLS (STARTTLS)</MenuItem>
                      <MenuItem value="ssl">SSL</MenuItem>
                      <MenuItem value="none">None</MenuItem>
                    </Select>
                  )}
                />
              </FormControl>
              <span className={`${styles.FieldHint}`}>TLS usually uses port 587, SSL uses 465.</span>
            </div>

            <div>
              <Controller
                name="smtp_port"
                control={control}
                rules={{
                  required: "Port is required",
                  validate: (v) => (Number.isInteger(Number(v)) && Number(v) >= 1 && Number(v) <= 65535) || "Enter a port between 1 and 65535",
                }}
                render={({ field }) => (<TextField {...field} label="Port*" variant="outlined" fullWidth inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }} onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ""))} error={!!errors.smtp_port} />)}
              />
              {errors.smtp_port && (<span className={`${styles.FieldError}`}>{errors.smtp_port.message}</span>)}
            </div>

            <p className={`${styles.GroupLabel}`}>Authentication</p>

            <div>
              <Controller
                name="smtp_username"
                control={control}
                render={({ field }) => (<TextField {...field} label="Username" variant="outlined" fullWidth autoComplete="off" />)}
              />
            </div>

            <div>
              <Controller
                name="smtp_password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Password"
                    placeholder={hasPassword ? "••••••••" : ""}
                    InputLabelProps={hasPassword ? { shrink: true } : undefined}
                    variant="outlined"
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    autoComplete="new-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end" aria-label={showPassword ? "Hide password" : "Show password"}>
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              <span className={`${styles.FieldHint}`}>{hasPassword ? "A password is saved. Leave blank to keep it." : "Stored encrypted."}</span>
            </div>

            <p className={`${styles.GroupLabel}`}>Sender</p>

            <div>
              <Controller
                name="smtp_from_address"
                control={control}
                rules={{ pattern: { value: EMAIL_REGEX, message: "Invalid email address" } }}
                render={({ field }) => (<TextField {...field} label="From Email" placeholder="bookings@yourcompany.com" variant="outlined" fullWidth error={!!errors.smtp_from_address} />)}
              />
              {errors.smtp_from_address
                ? <span className={`${styles.FieldError}`}>{errors.smtp_from_address.message}</span>
                : <span className={`${styles.FieldHint}`}>Defaults to the username if left blank.</span>}
            </div>

            <div>
              <Controller
                name="smtp_from_name"
                control={control}
                render={({ field }) => (<TextField {...field} label="From Name" variant="outlined" fullWidth />)}
              />
            </div>
          </div>
        </div>

        <div className={`${styles.CardFooter}`}>
          <button type="button" className={`${styles.ActionBU} ${styles.ActionGhost}`} onClick={openTest} disabled={saving}><Send /> Send Test Email</button>
          <button type="submit" className={`${styles.ActionBU} ${styles.ActionPrimary}`} disabled={saving}><Save /> {saving ? "Saving..." : "Save & Use SMTP"}</button>
        </div>
      </section>
    </form>

    <Dialog open={testOpen} onClose={() => !testing && setTestOpen(false)} fullWidth maxWidth="xs">
      <DialogTitle className={`${popupStyles.PopupTitleBar}`}>
        Send test email
        <IconButton onClick={() => setTestOpen(false)} className={`${popupStyles.PopupCloseBU}`} aria-label="Close" disabled={testing}><Close /></IconButton>
      </DialogTitle>
      <DialogContent className={`${popupStyles.PopupContent}`}>
        <form onSubmit={sendTest} className={`${styles.DialogBody}`} noValidate>
          <p className={`${styles.CardSubtitle}`} style={{ marginBottom: 16 }}>We’ll send a test message using the settings in the form (they don’t need to be saved first).</p>
          <TextField label="Send to*" type="email" value={testEmail} onChange={(e) => { setTestEmail(e.target.value); setTestError(""); }} fullWidth autoFocus error={!!testError} />
          {testError && (<span className={`${styles.FieldError}`}>{testError}</span>)}
          <div className={`${styles.DialogActions}`}>
            <button type="button" className={`${styles.ActionBU} ${styles.ActionGhost}`} onClick={() => setTestOpen(false)} disabled={testing}>Cancel</button>
            <button type="submit" className={`${styles.ActionBU} ${styles.ActionPrimary}`} disabled={testing}><Send /> {testing ? "Sending..." : "Send"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  </>);
};

export default SmtpSettings;
